import React from "react";
import { useNavigate } from "react-router-dom";

import { useMultiplierDataContext } from "@/store/MultiplierProvider";
import Typography from "@/ui/Typography/Typography";
import { formatMultiplierDate } from "@/pages/referralMultiplier/utils";
import {
  declinateOrders,
  getDaysText,
  pluralize,
  pluralizeWithCount,
  FRIENDS,
  FRIENDS_ACC,
  FRIENDS_GEN,
  ORDERS_INS,
  REMAINS,
  TIMES,
} from "@/utils/helpers";

import cls from "./styles.module.scss";

/** «в два раза больше», «в 3 раза больше», «в 5 раз больше» */
const timesLabel = (multiplier: number) =>
  multiplier === 2
    ? "в два раза"
    : `в ${multiplier} ${pluralize(multiplier, TIMES)}`;

export const ReferralMultiplierBanner = () => {
  const navigate = useNavigate();
  const { multiplierInfo } = useMultiplierDataContext();
  const targetReferrals = multiplierInfo?.target?.referrals_required ?? 0;
  const completedReferrals = multiplierInfo?.target?.referrals_done ?? 0;
  const ordersPerReferral = multiplierInfo?.target?.orders_per_referral ?? 0;
  const periodDays = multiplierInfo?.period_days ?? 30;
  const targetMultiplier =
    multiplierInfo?.target?.multiplier ?? multiplierInfo?.current.multiplier ?? 2;
  const activeMultiplier = multiplierInfo?.current.multiplier;
  const isActive = activeMultiplier !== null && activeMultiplier !== undefined;
  const isCollecting = !isActive && completedReferrals > 0;
  const remainingReferrals = Math.max(
    targetReferrals - completedReferrals,
    0
  );
  const expiryDate = formatMultiplierDate(
    multiplierInfo?.current.expires_at ?? null
  );
  const openConditions = () => {
    navigate("/referral-multiplier");
  };

  if (!multiplierInfo?.available) return null;

  const progressBar = (
    <span className={cls.progressBar}>
      {Array.from({ length: targetReferrals }).map((_, index) => (
        <span
          key={index}
          className={
            index < completedReferrals ? cls.progressComplete : undefined
          }
        />
      ))}
    </span>
  );

  return (
    <div className={cls.wrapper}>
      <button
        type="button"
        data-trigger="ref.main.multiplier"
        className={cls.banner}
        onClick={openConditions}
        aria-label="Узнать, как получать вдвойне больше за рефералку"
      >
        {!isActive && !isCollecting && (
          <span className={cls.multiplier}>x{targetMultiplier}</span>
        )}

        <span className={cls.content}>
          {isActive ? (
            <>
              <Typography component="span" className={cls.progressTitle}>
                <span>x{activeMultiplier} активен</span>
                {expiryDate && ` до ${expiryDate}`}
              </Typography>

              {targetReferrals > 0 && (
                <span className={cls.progressLayout}>
                  <span className={cls.progressBody}>
                    {/* Условие уже выполнено — шкала всегда полная; прогресс
                        продления показывает карточка «Продли ×N» ниже */}
                    <span className={cls.progressBar}>
                      {Array.from({ length: targetReferrals }).map(
                        (_, index) => (
                          <span key={index} className={cls.progressComplete} />
                        )
                      )}
                    </span>
                    <span className={cls.progressMeta}>
                      <span>
                        <strong>{targetReferrals}</strong> из {targetReferrals}
                      </span>
                    </span>
                  </span>
                  <span className={cls.progressArrow} aria-hidden="true">
                    ›
                  </span>
                </span>
              )}
            </>
          ) : isCollecting ? (
            <>
              <Typography component="span" className={cls.progressTitle}>
                До <span>x{targetMultiplier}</span>{" "}
                {pluralize(remainingReferrals, REMAINS)}{" "}
                <span>{pluralizeWithCount(remainingReferrals, FRIENDS)}</span>
              </Typography>

              <span className={cls.progressLayout}>
                <span className={cls.progressBody}>
                  {progressBar}
                  <span className={cls.progressMeta}>
                    <span>
                      <strong>{completedReferrals}</strong> из {targetReferrals}
                    </span>
                    <span>
                      выполнили по {declinateOrders(ordersPerReferral)}
                    </span>
                  </span>
                </span>
                <span className={cls.progressArrow} aria-hidden="true">
                  ›
                </span>
              </span>
            </>
          ) : (
            <>
              <Typography component="span" className={cls.title}>
                Получай {timesLabel(targetMultiplier)} больше за приведённого
                друга!
              </Typography>
              <span className={cls.footer}>
                <Typography component="span" className={cls.description}>
                  Пригласи{" "}
                  <span>
                    {pluralizeWithCount(targetReferrals, FRIENDS_ACC)}
                  </span>{" "}
                  с {pluralizeWithCount(ordersPerReferral, ORDERS_INS)} за{" "}
                  {getDaysText(periodDays)} — и получай множитель ×
                  {targetMultiplier} к своему бонусу
                </Typography>
                <span className={cls.arrow} aria-hidden="true">
                  ›
                </span>
              </span>
            </>
          )}
        </span>
      </button>

      {isActive && multiplierInfo.target && (
        <button
          type="button"
          data-trigger="ref.main.multiplier"
          className={cls.extendCard}
          onClick={openConditions}
          aria-label={`Продлить множитель ×${targetMultiplier}`}
        >
          <span className={cls.extendHead}>
            <Typography component="span" className={cls.extendTitle}>
              Продли ×{targetMultiplier} ещё на {periodDays} дней
            </Typography>
            <span className={cls.extendChip}>след. период</span>
          </span>

          <span className={cls.extendProgressRow}>
            <span className={cls.extendCount}>
              <strong>{completedReferrals}</strong> из {targetReferrals}{" "}
              {pluralize(targetReferrals, FRIENDS_GEN)}
            </span>
            <span className={cls.extendNote}>
              выполнили {declinateOrders(ordersPerReferral)}
            </span>
          </span>

          {progressBar}
        </button>
      )}
    </div>
  );
};
